import { StoreMinimalModel } from "./Store";
import { PersonMinimalModel } from "./Person";
import { OrderUnit } from "./Order";
import { ImageInformation } from "./ImageInformation";

export type CategoryId = string;
export type GLN = string;
export type ProductId = string;
export type DistributorId = string;
export enum ProductType {
  Misc = "Misc",
  Food = "Food"
}

// export interface ProductMiscDetails {
//   readonly size: string;
//   readonly description: string;
// }

export interface ProductFoodDetails {
  readonly size: string;
  readonly nutrientBasisQuantity: string;
  readonly nutritionalClaim: string;
  readonly nutrientDetail: string[];
  readonly gpcCategoryName: string;
  readonly ingredientStatement: string;
  readonly maximumStorageTemperature?: number;
  readonly minimumStorageTemperature?: number;
  readonly depositFee?: number;
  readonly durability?: number;
  readonly expectedDeliveryDurability?: number;
  readonly expectedDeliveryMinimumTemperature?: number;
  readonly expectedDeliveryMaximumTemperature?: number;
}

export interface ProductOrderInfo {
  distributor?: Distributor;
  readonly distributionPackageSize?: number;
  readonly durability?: number;
  orderUnit?: OrderUnit;
  readonly maxOnShelf?: number;
  readonly orderThreshold?: number;
}

export interface Variant {
  name: string;
  price: number;
  barcode: string;
  articleNumber: string;
  alternatives: [];
  productId: ProductId;
  tenantInventoryItemId: string;
  image?: ImageInformation;
}

export interface Product {
  readonly id: ProductId;
  readonly tenantInventoryItemId: string;
  readonly gtin: string;
  readonly type?: ProductType;
  readonly articleNumber?: string;
  supplier: Supplier;

  readonly name: string;
  readonly brandName: string;
  readonly description: string;

  readonly category: Category;
  readonly internalCategory?: Category;
  priority?: Priority;
  readonly section?: string;

  readonly pricing: ProductPricing;
  readonly sizing: ProductSizing;

  readonly comment?: string;
  readonly ageRestricted: boolean;
  readonly minimumAge?: number;
  readonly manualVerification: boolean;
  readonly imageUrl?: string;

  readonly created: Date;
  readonly updated: string;
  readonly createdBy: PersonMinimalModel;
  readonly lastUpdatedBy: PersonMinimalModel;

  readonly details: any;
  orderInfo?: ProductOrderInfo;
  readonly specificProductStocks?: SpecificProductStock[];
  readonly variants?: Variant[];
}

export interface ProductMinimalModel {
  id: ProductId;
  gtin: string;
  articleNumber?: string;
  name: string;
  brandName: string;
  size?: string;
  category: Category;
  priority?: Priority;
  maxOnShelf?: number;
  orderThreshold?: number;
  orderUnit?: OrderUnit;
  price: number;
  priceUnit: string;
}

export interface ProductPricing {
  price: number;
  priceUnit: string;
  comparisonPrice: number;
  comparisonPriceUnit: string;

  minimumPrice: number;
  isOpenPricing: boolean;

  vatPercentage?: number;
  vatAmount: number;
  priceExclVAT: number;

  acquisitionPrice: number;

  grossPriceMarginal: number;
  grossPriceMarginalPercentage: number;
  specificPriceExists: boolean;

  depositFee?: ProductDepositFee;
}

export interface ProductDepositFee {
  depositAmount: number;
  externalId?: string;
}

export interface ProductSizing {
  isExact?: boolean;
  quantity?: number;
  value?: number;
  unit?: string;
}

export interface ProductListModel {
  id: string;
  type?: ProductType;
  gtin: string;
  articleNumber: string;
  distributorName: string;
  name: string;
  brandName: string;
  categoryName: string;
  internalCategoryName: string;
  priorityName: string;
  section: string;
  supplierName: string;
  size: string;
  price: number;
  priceUnit: string;
  priceExclVAT: number;
  imageUrl: string;
}

export interface ProductOrderInfoWriteModel {
  readonly distributorId?: DistributorId;
  readonly distributionPackageSize?: number;
  readonly durability?: number;
  readonly orderUnit?: OrderUnit;
  readonly maxOnShelf?: number;
  readonly orderThreshold?: number;
}

export interface ProductWriteModel {
  gtin: string;
  type?: ProductType;
  articleNumber?: string;
  name: string;
  brandName: string;
  description: string;
  categoryId: CategoryId;
  internalCategoryId?: CategoryId;
  priorityId?: number;
  section?: string;
  supplier: Supplier;
  acquisitionPrice: number;
  price: number;
  priceUnit: string;
  minimumPrice: number;
  isOpenPricing: boolean;
  comparisonPrice: number;
  vatPercentage?: number;
  comment?: string;
  ageRestricted: boolean;
  minimumAge?: number;
  manualVerification: boolean;
  orderInfo?: ProductOrderInfoWriteModel;
  details: any;
}

export interface Supplier {
  gln: GLN;
  name: string;
}

export interface Distributor {
  id: DistributorId;
  name: string;
}

export interface Category {
  id: CategoryId;
  name: string;
  translation?: any;
  displayOrder?: number;
  isOrderable?: boolean;
  imageUrl?: string;
  productCount?: number;
}

export interface Tag {
  id: number;
  name: string;
  group: number;
  swedish?: string;
  english?: string;
}

export interface Priority {
  id: number;
  name: string;
}

export interface SpecificProductStock {
  storeId: string;
  storeName: string;

  onShelf: number;
  inStorage: number;
  hasPriceTag: boolean;
  isOrderable: boolean;

  exists: boolean;

  price?: number;
  maxOnShelf?: number;
  orderThreshold?: number;
  orderUnit?: OrderUnit;
  articleNumber?: string;
  priority?: Priority;
}

export interface ProductSyncResult {
  validationErrors: ReadonlyArray<string>;
  syncErrors: ReadonlyArray<string>;
  syncResult: ReadonlyArray<string>;
}

export interface ProductSpoilageReason {
  id: number;
  name: string;
  commentRequired: boolean;
}

export interface ProductStoreStatistic {
  store: StoreMinimalModel;
  quantitySold: number;
  valueSoldInclVat: number;
  valueSoldExclVat: number;
  latestSoldDate: Date;
}

export const toSize = (
  sizing: ProductSizing | undefined
): string | undefined => {
  if (
    !sizing ||
    sizing.quantity === undefined ||
    sizing.quantity < 1 ||
    sizing.value === undefined ||
    sizing.value <= 0 ||
    sizing.unit === undefined
  ) {
    return undefined;
  }

  return `${sizing.isExact ? "" : "ca "}${
    sizing.quantity > 1 ? `${sizing.quantity}x` : ""
  }${sizing.value} ${sizing.unit}`;
};

export const toProductWriteModel = (product: Product): ProductWriteModel => ({
  type: product.type,
  acquisitionPrice: product.pricing.acquisitionPrice,
  articleNumber: product.articleNumber,
  brandName: product.brandName,
  categoryId: product.category.id,
  internalCategoryId: product.internalCategory?.id,
  comparisonPrice: product.pricing.comparisonPrice,
  gtin: product.gtin,
  name: product.name,
  description: product.description,
  price: product.pricing.price,
  minimumPrice: product.pricing.minimumPrice,
  isOpenPricing: product.pricing.isOpenPricing,
  priceUnit: product.pricing.priceUnit,
  priorityId:
    product.priority && product.priority.id > 0
      ? product.priority.id
      : undefined,
  section: product.section,
  supplier: product.supplier,
  vatPercentage: product.pricing.vatPercentage,
  comment: product.comment,
  ageRestricted: product.ageRestricted,
  minimumAge: product.minimumAge,
  manualVerification: product.manualVerification,
  details: product.details,
  orderInfo: product.orderInfo
    ? {
        distributionPackageSize: product.orderInfo.distributionPackageSize,
        distributorId: product.orderInfo.distributor?.id,
        durability: product.orderInfo.durability,
        maxOnShelf: product.orderInfo.maxOnShelf,
        orderThreshold: product.orderInfo.orderThreshold,
        orderUnit: product.orderInfo.orderUnit
      }
    : undefined
});

export const emptyProduct = (gtin: string = ""): Product => ({
  id: "00000000-0000-0000-0000-000000000000",
  tenantInventoryItemId: "00000000-0000-0000-0000-000000000000",
  // type: ProductType.Misc,
  gtin: gtin,
  articleNumber: undefined,
  name: "",
  brandName: "",
  description: "",
  supplier: {
    gln: "",
    name: ""
  },
  category: {
    id: "",
    name: ""
  },
  priority: undefined,
  section: undefined,
  pricing: {
    acquisitionPrice: 0.0,
    price: 0.0,
    priceUnit: isWeightProduct(gtin) ? "kg" : "st",
    minimumPrice: 0.0,
    isOpenPricing: false,
    comparisonPrice: 0.0,
    comparisonPriceUnit: "kg",
    grossPriceMarginal: 0.0,
    grossPriceMarginalPercentage: 0.0,
    priceExclVAT: 0.0,
    vatAmount: 0.0,
    specificPriceExists: false
  },
  sizing: {},
  created: new Date(),
  updated: new Date().toDateString(),
  createdBy: {
    id: "00000000-0000-0000-0000-000000000000",
    name: "System"
  },
  lastUpdatedBy: {
    id: "00000000-0000-0000-0000-000000000000",
    name: "System"
  },
  ageRestricted: false,
  manualVerification: false,
  orderInfo: undefined,
  details: {
    size: "",
    description: undefined
  },
  comment: undefined
});

export const toProductListModel = (product: Product): ProductListModel => ({
  type: product.type,
  id: product.id,
  gtin: product.gtin,
  articleNumber: product.articleNumber ?? "...",
  brandName:
    product.brandName && product.brandName !== "" ? product.brandName : "...",
  name: product.name,
  categoryName: product.category.name,
  internalCategoryName: product.internalCategory?.name ?? "",
  distributorName: product.orderInfo?.distributor?.name ?? "...",
  priorityName: product.priority?.name ?? "...",
  section: product.section ?? "...",
  supplierName: product.supplier.name,
  size: product.details?.size ?? "...",
  price: product.pricing.price,
  priceExclVAT: product.pricing.priceExclVAT,
  priceUnit: product.pricing.priceUnit,
  imageUrl: product.imageUrl ?? ""
});

export const renderProductName = (
  product?: Product | ProductMinimalModel
): string => {
  if (!product) {
    return "";
  }

  return product.brandName
    ? `${product.brandName} ${product.name}`.trim()
    : product.name;
};

export const renderProductInfo = (product?: Product): string => {
  if (!product) {
    return "";
  }

  let rendered = "";

  if (product.details?.size) {
    if (rendered) {
      rendered += ", ";
    }

    rendered += product.details.size;
  }

  if (rendered) {
    rendered += ", ";
  }

  rendered += `gtin: ${product.gtin}`;

  rendered += `, art.nbr: ${
    product.articleNumber ? product.articleNumber : "..."
  }`;
  rendered += `, prio: ${product.priority ? product.priority.name : "..."}`;

  if (product.orderInfo?.distributionPackageSize) {
    rendered += `, DFP: ${product.orderInfo.distributionPackageSize}st`;
  }

  return rendered;
};

export const parseSizing = (size: string): ProductSizing | undefined => {
  if (!size) {
    return undefined;
  }
  try {
    // prepare
    size = size.trim().replaceAll(" ", "").toLowerCase();

    let isExact = true;
    let quantity = 1;
    let value = 1.0;

    // parse isExact
    if (size.startsWith("ca")) {
      size = size.substring(2);
      isExact = false;
    }

    // parse quantity
    if (size.indexOf("x") > -1) {
      let parts = size.split("x");
      quantity = Number(parts[0]);
      size = parts[1];
    }

    // parse value
    let regExp = /\b\d+([.,]\d+)?/g;
    if (size.match(regExp)) {
      let matches = regExp.exec(size);
      let match = matches && matches.length > 1 ? matches[0] : undefined;
      if (match) {
        value = Number(match);
        size = size.replaceAll(match, "");
      }
    }

    // rest is unit
    let unit = size;

    return {
      isExact,
      quantity,
      value,
      unit
    };
  } catch {
    return undefined;
  }
};

export const sizingToString = (
  sizing: ProductSizing | undefined
): string | undefined => {
  if (sizing === undefined) return undefined;

  if (sizing.isExact === undefined) return undefined;

  if (sizing.quantity === undefined) return undefined;

  if (sizing.value === undefined) return undefined;

  if (sizing.unit === undefined) return undefined;

  return `${sizing.isExact ? "" : "ca "}${
    sizing.quantity > 1 ? `${sizing.quantity}x` : ""
  }${sizing.value} ${sizing.unit.toLowerCase()}`;
};

export const isWeightProduct = (gtin: string) => {
  const regex = /^[0]?(20|21|22|23|24|25)[0-9]+$/;
  return regex.test(gtin);
};
